import { Component, inject, input, output, signal } from "@angular/core"
import { UiDialogComponent } from "ui-dialog"
import { MapService } from "../map/map.service"
import { UiIconComponent } from "ui-icon"
import { UiInheritDirective } from "ui-inherit"
import { ProfileService } from "../user-profile/profile.service"
import { JsonPipe } from "@angular/common"
import { faBug } from "@fortawesome/free-solid-svg-icons"

@Component({
  imports: [
    UiInheritDirective,
    UiIconComponent,
    UiDialogComponent,
    JsonPipe,
  ],
  standalone: true,
  template: `
    @if (showMapModal()) {
      <lib-ui-dialog (cancelAndClose)="hideMapModal.emit()">

        <div ngProjectAs="title">
          <div class="row-center w-full">
            Redlining Area Description
            @if (isAdmin()) {
              <button
                class="btn btn-sm btn-ghost text-primary text-2xl"
                [class]="showDescriptionData() ? 'outline outline-2' : ''"
                (click)="showDescriptionData.set(!showDescriptionData())"
              >
                <lib-ui-icon [icon]="faBug"/>
              </button>
            }
          </div>
        </div>

        <div ngProjectAs="content">
          <div
            style="transition: all 0.5s ease-out"
            (click)="$event.preventDefault()"
          >
<!--            [style.opacity]="showContent() ? 1 : 0"-->

            <div 
              class="column" 
            >
<!--              [style.opacity]="showContent() ? 1 : 0"-->
              <div class="column">
                @if (!regionProperties()) {
                  <div class="center">
                    This area does not have any
                    <a
                      href=""
                      style="text-decoration: underline"
                      (click)="showAttributionModal.emit()"
                    >
                      HOLC data.
                    </a>
                  </div>
                }
                @if (regionProperties(); as region) {
                  <div class="column p-4 shadow-lg shadow-spread-3 shadow-dark bg-[#f5f0e8] font-mono text-neutral">
                    @if (showDescriptionData()) {
                      <pre>{{ region | json }}</pre>
                    }
                    @if (!showDescriptionData()) {
                      @if (!region.grade && !region.occupation_or_type) {
                        <div class="center">
                          This area does not have any
                          <a
                            href=""
                            style="text-decoration: underline"
                            (click)="showAttributionModal.emit()"
                          >
                            HOLC data.
                          </a>
                        </div>
                      }
                      @if (region.grade && !region.occupation_or_type) {
                        <div class="mb-6">
                          Although we have the geospatial redlining data for this zone, the original Area Descriptions
                          are not available in our National Archives dataset and no one knows what happened to these
                          documents. Got a lead on where we can get these historical records?
                          <a href="mailto:info@designingthewe.com">
                            Let us know!
                          </a>
                        </div>
                        @if (region.grade === "A") {
                          <div>
                            GRADE A: Green areas were considered to be the most desirable, and always were home to those
                            at the top of a false idea of racial hierarchy.
                          </div>
                          <div>
                            These were areas that often had strict restrictions on who could live in the area, and these
                            were enforced legally with neighborhood covenants or in the deeds of the homes. When these
                            restrictions became illegal in 1968, racial steering, single family zoning and intimidation
                            were often used to preserve ‘homogeneity.’ Green areas were usually exclusively wealthy
                            neighborhoods as well, with many area descriptions throughout the country describing the
                            inhabitants simply as “capitalists.
                          </div>
                        }
                        @if (region.grade === "B") {
                          <div>
                            GRADE B: Blue areas were second best. They were often still homogeneously White and
                            considered ‘desirable’ if not the most wealthy areas.
                          </div>
                          <div>
                            Blue areas didn’t make the cut to be green. What makes an area green is not just to say; yes
                            this is where White people are living. Often, a green rating also required the presence of
                            restrictions preventing the ‘infiltration’ of ‘hazardous’ people. White areas without these
                            restrictions, or that were too close to ‘hazardous’ neighborhoods, were often colored in
                            blue: second best. Over time, some blue areas slipped into decline as well, while others
                            retained their value. This often depended on the greater context of the surrounding areas.
                          </div>
                        }
                        @if (region.grade === "C") {
                          <div>
                            GRADE C: Yellow areas were deemed to be in decline, and often didn’t fare much better than
                            their redlined counterparts.
                          </div>
                          <div>
                            The maps and area descriptions placed ‘negroes’ at the bottom of a false racial hierarchy.
                            But even for those ‘one rung up,’ the maps also devalued many groups of people, including
                            Hispanic families, Asian families, and those labeled ‘foriegn born,’ which at that time
                            included Italian immigrants, Eastern Europeans, Jewish families and others.
                          </div>
                          <div>
                            When the idea of race eventually became primarily one of skin color and European origin,
                            some of these groups would be re-considered as White, granting access to segregated suburbs.
                            Meanwhile others would remain ‘people of color’ in red or yellow districts.
                          </div>
                        }
                        @if (region.grade === "D") {
                          <div>
                            GRADE D: Once Redlined, these neighborhoods became destined for disinvestment and
                            concentrated poverty.
                          </div>
                          <div>
                            Redlining was destabilizing for populations who were subsequently cut off from mechanisms to
                            join the middle-class, like home ownership and education programs like those in the G.I.
                            Bill, which offered free higher education to returning veterans from WWII. Before the 1968
                            Fair Housing Act, 98% of federally backed home loans went only to White families.
                          </div>
                          <div>
                            As we will see in the Section 3 Timeline, redlining was followed by destructive programs
                            like Urban Renewal and Planned Shrinkage, while practices like contract buys and predatory
                            banking exploited those who lived there. This disruption led to epidemics of drugs, disease
                            and crime that further tore apart communities and justified the mass incarceration of young
                            men of color.
                          </div>
                        }
                      }
                      @if (region.grade && region.occupation_or_type) {
                        <div class="center">
                          <a
                            href=""
                            class="underline"
                            (click)="showAttributionModal.emit()"
                          >
                            Home Owners' Loan Corporation
                          </a>
                          <div class="text-xs">
                            partial transcription from 1935-1940
                          </div>
                        </div>

                        <div class="column gap-4 p-8">
                          <div>
                            NAME OF CITY:
                            {{ region.city }}
                            <!--          ({{ region.location || region.name }})-->
                          </div>
                          <div>
                            SECURITY GRADE:
                            {{ region.grade }}
                          </div>
                          <div>
                            AREA NO.:
                            {{ region.area_number }}
                          </div>
                          <div>
                            DESCRIPTION OF TERRAIN:
                            {{ region.description_of_terrain }}
                          </div>
                          @if (region.favorable_influences) {
                            <div>
                              FAVORABLE INFLUENCES:
                              {{ region.favorable_influences }}
                            </div>
                          }
                          @if (region.detrimental_influences) {
                            <mark>
                              DETRIMENTAL INFLUENCES:
                              {{ region.detrimental_influences }}
                            </mark>
                          }
                          @if (region.occupation_or_type) {
                            <div class="column gap-0">
                              <div>
                                INHABITANTS:
                              </div>
                              <div class="pl-8">
                                a. Type:
                                {{ region.occupation_or_type }}
                              </div>
                              <div class="pl-8">
                                b. Estimated Annual Family Income:
                                {{ region.estimated_annual_family_income }}
                              </div>
                              <div class="pl-8">
                                c. Foreign-Born: Nationality:
                                {{ region.foreign_born_nationality }}; {{ region.foreign_born_percent }}%
                              </div>
                              <mark class="pl-8">
                                d. Negro (Yes or No):
                                {{ region.negro_yes_or_no }};
                                {{ region.negro_percent }}%
                              </mark>
                              <mark class="pl-8">
                                e. Infiltration of:
                                {{ region.infiltration_of }}
                              </mark>
                            </div>
                          }
                          @if (region.occupancy) {
                            <div class="column gap-0">
                              <div>
                                OCCUPANCY:
                              </div>
                              <div class="pl-8">
                                a. Land:
                                {{ region.occupancy }}%
                              </div>
                              <div class="pl-8">
                                b. Dwelling units:
                                {{ region.dwelling_units }}%
                              </div>
                              <div class="pl-8">
                                c. Home ownership:
                                {{ region.home_ownership }}%
                              </div>
                            </div>
                          }
                          @if (region.availability_of_mortgage_funds_home_purchase) {
                            <div class="column gap-0">
                              <div>
                                AVAILABILITY OF MORTGAGE FUNDS:
                              </div>
                              <div class="pl-8">
                                a. Home purchase:
                                {{ region.availability_of_mortgage_funds_home_purchase }};
                              </div>
                              <div class="pl-8">
                                b. Home building:
                                {{ region.availability_of_mortgage_funds_home_building }}
                              </div>
                            </div>
                          }
                          <div>
                            TREND OF DESIRABILITY: Next 10-15 years:
                            {{ region.trend_of_desirability }}
                          </div>
                          <div style="text-indent: -20px">
                            <div style="padding-left: 20px">
                              CLARIFYING REMARKS:
                              {{ region.clarifying_remarks }}
                            </div>
                          </div>
                          @if (region.information_obtained_from) {
                            <div>
                              Information for this form was obtained from
                              {{ region.information_obtained_from }}
                            </div>
                          }
                          @if (region.information_obtained_month_and_day) {
                            <div>
                              Date:
                              {{ region.information_obtained_month_and_day }}
                              @if (region.information_obtained_193x) {
                                193{{ region.information_obtained_193x }}
                              }
                            </div>
                          }

                        </div>
                      }
                    }
                  </div>
                }
                <div style="text-align: right; font-size: 12px; font-style: italic">
                  \u2014 data from the
                  <a
                    href=""
                    style="text-decoration: underline"
                    (click)="showAttributionModal.emit()"
                  >
                    Mapping Inequality Project
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>

      </lib-ui-dialog>
    }

    <!--
          <ng-template pTemplate="header">
            <lib-ui-icon [icon]="faArrowsAlt"/>
            <div class="center wide">
              Learn - Discover - Share
            </div>
          </ng-template>
          <ng-template pTemplate="footer"/>
    -->
  `,
  selector: "e2e-redline-map-details",
})
export class RedlineMapDetailsComponent {
  private redlineMapService = inject(MapService)
  private profileService = inject(ProfileService)

  isAdmin = this.profileService.isAdmin

  showMapModal = input.required()
  // showContent = input.required()
  hideMapModal = output<void>()
  showAttributionModal = output<void>()
  createNewContent = output<void>()

  showDescriptionData = signal(false)
  regionProperties = this.redlineMapService.regionProperties
  storyProperties = this.redlineMapService.storyProperties

  protected readonly faBug = faBug
}

/**
 *
 *
 *   styles: `
 *     .number {
 *       border: 1px solid white;
 *       border-radius: 2em;
 *       width: 2em;
 *       height: 2em;
 *       line-height: 2em;
 *       text-align: center;
 *       font-size: 1.2em;
 *       color: white;
 *       box-sizing: border-box;
 *       flex: 0 0 2em;
 *     }
 *
 *     .header {
 *       font-size: 1.2em;
 *       line-height: 2em;
 *       font-weight: bold;
 *       color: white;
 *       flex: 1;
 *     }
 *   `,
 *
 *
 *         <!--
 *                 <b>
 *                   {{ region.location || region.name }}
 *                   {{ region.security_grade }}
 *                   {{ region.area_number }}
 *                   {{ region.date }}
 *                 </b>
 *                 <p>
 *                   <i>
 *                     Class and Occupation
 *                   </i>
 *                   <br/>
 *                   {{ region.occupation_or_type }}
 *                 </p>
 *                 <p>
 *                   <i>
 *                     Foreign Families (Nationality)
 *                   </i>
 *                   <br/>
 *                   {{ region.foreign_born_percent }}
 *                 </p>
 *                 <p>
 *                   <i>
 *                     Negro Infiltration
 *                   </i>
 *                   <br/>
 *                   {{ region.negro_percent || '-' }}
 *                 </p>
 *                 <p>
 *                   <i>
 *                     Shifting or Infiltration
 *                   </i>
 *                   <br/>
 *                   {{ region.infiltration_of || '-' }}
 *                 </p>
 *                 <p>
 *                   <i>
 *                     Description
 *                   </i>
 *                   <br/>
 *                   {{ region.clarifying_remarks }}
 *                 </p>
 *         -->
 *
 *
 *
 *             <!--
 *             <div
 *               class="column red-paper-texture"
 *               style="padding: 10px; width: 320px"
 *             >
 *
 *               &lt;!&ndash;
 *                         @if (learnMode() || !handset() || true) {
 *                           <section
 *                             class="column"
 *                             [style.flex]="learnMode() ? 1 : 0"
 *                           >
 *                             <div
 *                               class="row"
 *                               (click)="switchToLearnMode()"
 *                             >
 *                               <div class="number">
 *                                 1
 *                               </div>
 *                               <div class="header">
 *                                 Learn about Redlining
 *                               </div>
 *                             </div>
 *                             @if (learnMode()) {
 *                               <e2e-local-map-learn/>
 *                             }
 *                           </section>
 *                         }
 *               &ndash;&gt;
 *
 *               &lt;!&ndash;
 *                         @if (storyMode() || !handset() || true) {
 *                           <section
 *                             class="column"
 *                             [style.flex]="storyMode() ? 1 : 0"
 *                           >
 *                             <div
 *                               class="row"
 *                               (click)="switchToStoryMode()"
 *                             >
 *                               <div class="number">
 *                                 2
 *                               </div>
 *                               <div class="header">
 *                                 Tell Your Story
 *                               </div>
 *                             </div>
 *                             @if (storyMode()) {
 *                               <e2e-local-map-story/>
 *                             }
 *                           </section>
 *                         }
 *               &ndash;&gt;
 *
 *               &lt;!&ndash;
 *                         @if (discoverMode() || !handset() || true) {
 *                           <section
 *                             class="column"
 *                             [style.flex]="discoverMode() ? 1 : 0"
 *                           >
 *                             <div
 *                               class="row"
 *                               (click)="switchToDiscoverMode()"
 *                             >
 *                               <div class="number">
 *                                 3
 *                               </div>
 *                               <div class="header">
 *                                 Discover Stories
 *                               </div>
 *                             </div>
 *                             @if (discoverMode()) {
 *                               <e2e-local-map-discover/>
 *                             }
 *                           </section>
 *                         }
 *               &ndash;&gt;
 *
 *               <button routerLink="/timeline" style="margin-top: auto">
 *                 <e2e-next-section-button/>
 *               </button>
 *
 *             </div>
 *     -->
 */
